import { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  useAcceptance,
  useSubmitAcceptance,
  useIssueAcceptanceEinvoice,
  useNotifyAcceptanceLine,
} from '@saiens/shared/hooks';
import {
  downloadPackagePdf,
  downloadEinvoicePdf,
  getPaymentData,
} from '@saiens/shared/api/services/acceptance.service';
import { toast } from '@saiens/shared/stores/toast.store';
import type { PaymentRequestData } from '@saiens/shared/types';
import { formatDate } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { PaymentRequestPdf } from './PaymentRequestPdf';
import { AcceptanceReportPdf } from './AcceptanceReportPdf';
import {
  FileText,
  Download,
  Send,
  Edit,
  CheckCircle,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Package,
  Receipt,
  Ticket,
  ExternalLink,
  MessageCircle,
  Copy,
  Share2,
} from 'lucide-react';

// 業務單簽新狀態機：draft(待簽名) → signed(驗收完成待送出) → invoiced(已建憑單) → done
const STATE_LABELS: Record<string, string> = {
  draft: '待簽名',
  signed: '驗收完成待送出',
  submitted: '已送出',
  invoiced: '已建憑單',
  done: '完成',
  cancelled: '已取消',
};

const INVOICE_STATE_LABELS: Record<string, string> = {
  draft: '草稿',
  posted: '已過帳',
  cancel: '已取消',
};

const EINVOICE_STATE_LABELS: Record<string, string> = {
  'to invoice': '待開立',
  invoiced: '已開立',
  Discounted: '已折讓',
  invalid: '已作廢',
};

// 色彩收斂：可行動狀態用藍，完成類黑底白字，其餘灰階
const STATE_COLORS: Record<string, string> = {
  draft: 'bg-primary/10 text-primary',
  signed: 'bg-primary/10 text-primary',
  submitted: 'bg-[#e8e8ed] text-[#6e6e73]',
  invoiced: 'bg-[#1d1d1f] text-white',
  done: 'bg-[#1d1d1f] text-white',
  cancelled: 'bg-[#e8e8ed] text-[#6e6e73]',
};

const WARRANTY_STATUS_LABELS: Record<string, string> = {
  link_issued: '已發連結',
  registered: '已註冊',
};

const WARRANTY_STATUS_COLORS: Record<string, string> = {
  link_issued: 'bg-primary/10 text-primary',
  registered: 'bg-[#1d1d1f] text-white',
};

const SECONDARY_BTN_CLASS =
  'flex min-h-[44px] items-center justify-center gap-1.5 rounded-full bg-white px-4 py-2.5 text-sm font-medium text-[#1d1d1f] ring-1 ring-inset ring-[#d2d2d7] disabled:opacity-50';

const ntdFormatter = new Intl.NumberFormat('zh-TW');
const ntd = (n: number | null | undefined): string =>
  n == null ? '---' : `NT$${ntdFormatter.format(n)}`;

export function AcceptanceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? parseInt(id, 10) : undefined;
  const [showOrderLines, setShowOrderLines] = useState(false);
  const [pdfLoading, setPdfLoading] = useState<'pdf' | 'package' | 'payment' | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentRequestData | null>(null);
  const [reportData, setReportData] = useState<PaymentRequestData | null>(null);
  const [einvoicePdfUrl, setEinvoicePdfUrl] = useState<string | null>(null);
  const pdfRef = useRef<HTMLDivElement>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  // 送出驗收前的五項核對 dialog
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitChecked, setSubmitChecked] = useState(false);

  const { data, isLoading, error } = useAcceptance(numericId);
  const submitMutation = useSubmitAcceptance();
  const issueEinvoiceMutation = useIssueAcceptanceEinvoice();
  const notifyLineMutation = useNotifyAcceptanceLine();

  const handleNotifyLine = () => {
    if (!data) return;
    if (
      !window.confirm(
        '確定要發送 LINE 訊息給客戶？\n\n訊息含請款包 + 電子發票下載連結，可重複發送。',
      )
    )
      return;
    notifyLineMutation.mutate(data.id);
  };

  const handleCopyWarrantyLink = async () => {
    if (!data?.warranty_link) return;
    try {
      await navigator.clipboard.writeText(data.warranty_link);
      toast.success('已複製', '保固註冊連結已複製');
    } catch {
      toast.error('複製失敗', '請手動選取連結複製');
    }
  };

  // 公開頁已改為純檢視頁：分享的是驗收結果連結（次要動作）
  const handleCopyShareLink = async () => {
    if (!data?.access_token) return;
    const url = `${window.location.origin}/acceptance/sign/${data.access_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('已複製', '驗收結果連結已複製，可傳送給客戶查看');
    } catch {
      toast.error('複製失敗', '請稍後再試');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-12 text-center text-red-500">
        {error instanceof Error ? error.message : '載入失敗'}
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!numericId || !submitChecked) return;
    try {
      await submitMutation.mutateAsync(numericId);
    } catch {
      // Error handled by onError callback
    } finally {
      setShowSubmitDialog(false);
      setSubmitChecked(false);
    }
  };

  const handleIssueEinvoice = async () => {
    if (!numericId) return;
    try {
      const result = await issueEinvoiceMutation.mutateAsync(numericId);
      if (result.einvoice_pdf_url) {
        setEinvoicePdfUrl(result.einvoice_pdf_url);
      }
    } catch {
      // Error handled by onError callback
    }
  };

  const handleDownloadEinvoicePdf = async () => {
    if (!numericId || pdfLoading) return;
    setPdfLoading('package');
    try {
      await downloadEinvoicePdf(numericId);
    } catch (err) {
      toast.error('下載失敗', (err as { message?: string })?.message || '電子發票 PDF 下載失敗');
    } finally {
      setPdfLoading(null);
    }
  };

  const handleDownloadPdf = async () => {
    if (!numericId || pdfLoading) return;
    setPdfLoading('pdf');
    try {
      const rData = await getPaymentData(numericId);
      setReportData(rData);

      const element = await new Promise<HTMLDivElement | null>((resolve) => {
        let attempts = 0;
        const check = () => {
          if (reportRef.current) {
            resolve(reportRef.current);
          } else if (attempts < 30) {
            attempts++;
            requestAnimationFrame(check);
          } else {
            resolve(null);
          }
        };
        requestAnimationFrame(check);
      });
      if (!element) return;

      const html2pdf = (await import('html2pdf.js')).default;
      const pdfBlob = await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(element)
        .outputPdf('blob');
      const url = URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      toast.error('PDF 失敗', (err as { message?: string })?.message || '驗收報告 PDF 產生失敗');
    } finally {
      setPdfLoading(null);
      setReportData(null);
    }
  };

  const handleDownloadPackage = async () => {
    if (!numericId || pdfLoading) return;
    setPdfLoading('package');
    try {
      await downloadPackagePdf(numericId);
    } catch (err) {
      toast.error('下載失敗', (err as { message?: string })?.message || '請款包 PDF 下載失敗');
    } finally {
      setPdfLoading(null);
    }
  };

  const handleDownloadPayment = async () => {
    if (!numericId || pdfLoading) return;
    setPdfLoading('payment');
    try {
      const pData = await getPaymentData(numericId);
      setPaymentData(pData);

      // Wait for component to render via ref polling
      const element = await new Promise<HTMLDivElement | null>((resolve) => {
        let attempts = 0;
        const check = () => {
          if (pdfRef.current) {
            resolve(pdfRef.current);
          } else if (attempts < 30) {
            attempts++;
            requestAnimationFrame(check);
          } else {
            resolve(null);
          }
        };
        requestAnimationFrame(check);
      });
      if (!element) return;

      const html2pdf = (await import('html2pdf.js')).default;
      const pdfBlob = await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(element)
        .outputPdf('blob');
      const url = URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      toast.error('PDF 失敗', (err as { message?: string })?.message || '請款單 PDF 產生失敗');
    } finally {
      setPdfLoading(null);
      setPaymentData(null);
    }
  };

  const mutationError = submitMutation.error;
  const canShare = !!data.access_token && data.has_employee_signature;

  return (
    <div className="px-4 py-6 pb-32">
      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold tracking-tight text-[#1d1d1f]">
            {data.name}
          </h1>
          <p className="mt-0.5 truncate text-sm text-[#86868b]">{data.project_name}</p>
        </div>
        <span
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${STATE_COLORS[data.state] || 'bg-[#e8e8ed] text-[#6e6e73]'}`}
        >
          {STATE_LABELS[data.state] || data.state}
        </span>
      </div>

      {/* Basic Info */}
      <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
        <h2 className="mb-3 text-[15px] font-semibold text-[#1d1d1f]">基本資訊</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <InfoItem label="客戶" value={data.partner_name} />
          <InfoItem label="施工地址" value={data.project_addr} />
          <InfoItem label="完工日期" value={formatDate(data.completion_date)} />
          <InfoItem label="填表人" value={data.employee_name} />
          <InfoItem
            label="平均分數"
            value={`${data.total_score.toFixed(1)} / 5`}
          />
          <InfoItem label="尾款金額" value={ntd(data.balance_amount)} />
        </div>
      </section>

      {/* Order Lines (collapsible) */}
      {data.order_lines && data.order_lines.length > 0 && (
        <section className="mb-4 rounded-2xl bg-[#f5f5f7]">
          <button
            type="button"
            onClick={() => setShowOrderLines((prev) => !prev)}
            className="flex min-h-[44px] w-full items-center justify-between p-4"
          >
            <h2 className="flex items-center gap-1.5 text-[15px] font-semibold text-[#1d1d1f]">
              <Package className="h-4 w-4 text-[#86868b]" />
              訂單項目 ({data.order_lines.length})
            </h2>
            {showOrderLines ? (
              <ChevronUp className="h-5 w-5 text-[#86868b]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[#86868b]" />
            )}
          </button>
          {showOrderLines && (
            <div className="px-4 pb-4">
              <div className="space-y-2">
                {data.order_lines.map((line) => (
                  <div
                    key={line.id}
                    className="flex items-center justify-between rounded-xl bg-white px-3 py-2 text-sm"
                  >
                    <span className="flex-1 text-[#1d1d1f]">{line.product_name}</span>
                    <div className="ml-3 shrink-0 text-right text-xs tabular-nums text-[#86868b]">
                      <span>{line.qty_delivered}/{line.product_uom_qty}</span>
                      {line.product_uom && (
                        <span className="ml-0.5">{line.product_uom}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-right text-xs text-[#86868b]">
                已交貨/訂購數量
              </p>
            </div>
          )}
        </section>
      )}

      {/* Financial Info */}
      <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
        <h2 className="mb-3 text-[15px] font-semibold text-[#1d1d1f]">財務資訊</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-[#86868b]">訂單總額</span>
            <span className="tabular-nums text-[#1d1d1f]">{ntd(data.amount_total)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#86868b]">已收訂金</span>
            <span className="tabular-nums text-[#6e6e73]">
              -{ntd(data.deposit_amount)}
            </span>
          </div>
          <div className="flex justify-between border-t border-[#d2d2d7] pt-2">
            <span className="font-medium text-[#1d1d1f]">尾款金額</span>
            <span className="font-semibold tabular-nums text-[#1d1d1f]">
              {ntd(data.balance_amount)}
            </span>
          </div>
        </div>
      </section>

      {/* Payment Received Info */}
      {(data.payment_received_date || data.payment_account_last5 || data.payment_received_amount) && (
        <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
          <h2 className="mb-3 text-[15px] font-semibold text-[#1d1d1f]">收款資訊</h2>
          <div className="space-y-2 text-sm">
            {data.payment_received_date && (
              <div className="flex justify-between">
                <span className="text-[#86868b]">收款日期</span>
                <span className="tabular-nums text-[#1d1d1f]">{data.payment_received_date}</span>
              </div>
            )}
            {data.payment_account_last5 && (
              <div className="flex justify-between">
                <span className="text-[#86868b]">匯款帳號後五碼</span>
                <span className="tabular-nums text-[#1d1d1f]">{data.payment_account_last5}</span>
              </div>
            )}
            {data.payment_received_amount != null && data.payment_received_amount > 0 && (
              <div className="flex justify-between">
                <span className="text-[#86868b]">收款金額</span>
                <span className="font-medium tabular-nums text-[#1d1d1f]">
                  {ntd(data.payment_received_amount)}
                </span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Inspection Items */}
      <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
        <h2 className="mb-3 text-[15px] font-semibold text-[#1d1d1f]">驗收項目</h2>
        <div className="space-y-2">
          {data.items?.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between text-sm"
            >
              <span className="text-[#1d1d1f]">{item.name}</span>
              {item.status === 'not_applicable' ? (
                <span className="shrink-0 text-xs font-medium text-[#86868b]">未施作</span>
              ) : (
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <div
                      key={s}
                      className={`h-5 w-5 rounded-full text-center text-xs leading-5 tabular-nums ${
                        item.score === s
                          ? 'bg-primary text-white'
                          : 'bg-white text-[#86868b]'
                      }`}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Notes */}
      {data.completion_note && (
        <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
          <h2 className="mb-2 text-[15px] font-semibold text-[#1d1d1f]">完工紀錄</h2>
          <p className="whitespace-pre-wrap text-sm text-[#393c41]">
            {data.completion_note}
          </p>
        </section>
      )}

      {/* 驗收簽名：業務單簽制，客戶簽名僅歷史舊單顯示 */}
      <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
        <h2 className="mb-3 text-[15px] font-semibold text-[#1d1d1f]">驗收簽名</h2>
        <div className="text-sm">
          <p className="text-[#86868b]">業務</p>
          {data.has_employee_signature ? (
            <div className="mt-1 flex items-center gap-1.5 text-[#1d1d1f]">
              <CheckCircle className="h-4 w-4" /> 已簽名完成驗收
              <span className="text-xs text-[#86868b]">
                {formatDate(data.employee_signed_date)}
              </span>
            </div>
          ) : (
            <div className="mt-1 flex items-center gap-1.5 text-[#86868b]">
              <Clock className="h-4 w-4" /> 尚未簽名
            </div>
          )}
        </div>


        {/* 次要動作：分享檢視用的驗收結果連結 */}
        {canShare && (
          <button
            type="button"
            onClick={handleCopyShareLink}
            className="mt-3 flex min-h-[44px] items-center gap-1.5 text-sm font-medium text-primary"
          >
            <Share2 className="h-4 w-4" />
            分享驗收結果連結
          </button>
        )}
      </section>

      {data.warranty_link && (
        <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-[15px] font-semibold text-[#1d1d1f]">保固註冊連結</h2>
            {data.warranty_status && data.warranty_status !== 'none' && (
              <span
                className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
                  WARRANTY_STATUS_COLORS[data.warranty_status] || 'bg-[#e8e8ed] text-[#6e6e73]'
                }`}
              >
                {WARRANTY_STATUS_LABELS[data.warranty_status] || data.warranty_status}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <a
              href={data.warranty_link}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate rounded-xl bg-white px-3 py-2.5 text-sm text-primary"
            >
              {data.warranty_link}
            </a>
            <button
              type="button"
              onClick={handleCopyWarrantyLink}
              className={`shrink-0 ${SECONDARY_BTN_CLASS}`}
            >
              <Copy className="h-4 w-4" />
              複製
            </button>
          </div>
        </section>
      )}

      {/* Invoice Info */}
      {data.invoice_id && (
        <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
          <h2 className="mb-3 text-[15px] font-semibold text-[#1d1d1f]">應收憑單</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#86868b]">憑單編號</span>
              <span className="text-[#1d1d1f]">{data.invoice_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#86868b]">狀態</span>
              <span className="text-[#1d1d1f]">
                {INVOICE_STATE_LABELS[data.invoice_state ?? ''] || data.invoice_state}
              </span>
            </div>
            {data.invoice_date && (
              <div className="flex justify-between">
                <span className="text-[#86868b]">發票日期</span>
                <span className="tabular-nums text-[#1d1d1f]">{formatDate(data.invoice_date)}</span>
              </div>
            )}
          </div>
          {data.invoice_lines && data.invoice_lines.length > 0 && (
            <div className="mt-3 border-t border-[#d2d2d7] pt-3">
              <div className="space-y-2">
                {data.invoice_lines.map((line) => (
                  <div key={line.id} className="flex items-start justify-between text-sm">
                    <span className="flex-1 text-[#393c41]">{line.name}</span>
                    <span className="ml-2 shrink-0 tabular-nums text-[#1d1d1f]">
                      {ntd(line.price_subtotal ?? 0)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-2 space-y-1 border-t border-[#d2d2d7] pt-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#86868b]">未稅</span>
                  <span className="tabular-nums text-[#1d1d1f]">{ntd(data.invoice_amount_untaxed)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#86868b]">稅額</span>
                  <span className="tabular-nums text-[#1d1d1f]">{ntd(data.invoice_amount_tax)}</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span className="text-[#1d1d1f]">合計</span>
                  <span className="tabular-nums text-[#1d1d1f]">{ntd(data.invoice_amount_total)}</span>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* E-Invoice Section */}
      {(data.einvoice_number || data.invoice_id) && (
        <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
          <h2 className="mb-3 text-[15px] font-semibold text-[#1d1d1f]">電子發票</h2>

          {data.einvoice_number ? (
            <>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#86868b]">發票號碼</span>
                  <span className="font-medium tabular-nums text-[#1d1d1f]">{data.einvoice_number}</span>
                </div>
                {data.einvoice_state && (
                  <div className="flex justify-between">
                    <span className="text-[#86868b]">狀態</span>
                    <span className="text-[#1d1d1f]">
                      {EINVOICE_STATE_LABELS[data.einvoice_state] ?? data.einvoice_state}
                    </span>
                  </div>
                )}
              </div>

              {/* PDF actions: view + download */}
              {data.einvoice_state === 'invoiced' && (
                <div className="mt-3 flex gap-2">
                  {einvoicePdfUrl && (
                    <a
                      href={einvoicePdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex-1 ${SECONDARY_BTN_CLASS}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                      查看
                    </a>
                  )}
                  <button
                    onClick={handleDownloadEinvoicePdf}
                    disabled={pdfLoading === 'package'}
                    className={`flex-1 ${SECONDARY_BTN_CLASS}`}
                  >
                    <Ticket className="h-4 w-4" />
                    {pdfLoading === 'package' ? '下載中...' : '下載'}
                  </button>
                </div>
              )}
            </>
          ) : (
            /* Invoice exists but no e-invoice yet */
            <div className="space-y-3">
              <p className="text-sm text-[#86868b]">
                {data.can_issue_einvoice ? '尚未開立電子發票' : '尚未開立電子發票（待會計過帳）'}
              </p>
              {data.can_issue_einvoice && (
                <button
                  onClick={handleIssueEinvoice}
                  disabled={issueEinvoiceMutation.isPending}
                  className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50"
                >
                  <Ticket className="h-4 w-4" />
                  {issueEinvoiceMutation.isPending ? '開立中...' : '開電子發票'}
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {/* 送出驗收前：五項核對 Dialog */}
      {showSubmitDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="max-h-[85vh] w-full max-w-sm overflow-y-auto rounded-2xl bg-white p-6">
            <h3 className="mb-3 text-base font-semibold text-[#1d1d1f]">
              送出前請逐項核對
            </h3>
            <p className="mb-4 text-sm text-[#393c41]">
              送出後將自動建立應收憑單，請先確認以下資料無誤。
            </p>

            <div className="mb-4 space-y-2 rounded-xl bg-[#f5f5f7] p-3 text-sm">
              <div className="flex justify-between gap-3">
                <span className="shrink-0 text-[#86868b]">客戶公司名稱</span>
                <span className="text-right font-medium text-[#1d1d1f]">
                  {data.invoice_partner_name || data.partner_name || '—'}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="shrink-0 text-[#86868b]">統編</span>
                <span className="text-right font-medium tabular-nums text-[#1d1d1f]">
                  {data.invoice_vat ? data.invoice_vat : '—（將以個人名義開立）'}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="shrink-0 text-[#86868b]">案名</span>
                <span className="text-right font-medium text-[#1d1d1f]">
                  {data.project_name || '—'}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="shrink-0 text-[#86868b]">
                  金額（{data.deposit_amount === 0 ? '全款' : '尾款'}）
                </span>
                <span className="text-right font-semibold tabular-nums text-[#1d1d1f]">
                  {ntd(data.balance_amount)}
                </span>
              </div>
              <div className="border-t border-[#e8e8ed] pt-2">
                <p className="mb-1 text-[#86868b]">品項名稱</p>
                {data.order_lines && data.order_lines.length > 0 ? (
                  <ul className="space-y-1">
                    {data.order_lines.map((line) => (
                      <li key={line.id} className="text-[#1d1d1f]">
                        {line.product_name || '—'}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[#1d1d1f]">—</p>
                )}
              </div>
            </div>

            <label className="mb-6 flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                checked={submitChecked}
                onChange={(e) => setSubmitChecked(e.target.checked)}
              />
              <span className="text-sm text-[#393c41]">我已核對以上資料無誤</span>
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setShowSubmitDialog(false); setSubmitChecked(false); }}
                className={`flex-1 ${SECONDARY_BTN_CLASS}`}
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!submitChecked || submitMutation.isPending}
                className="min-h-[44px] flex-1 rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                {submitMutation.isPending ? '送出中...' : '確認送出'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {(mutationError || issueEinvoiceMutation.error) && (
        <section className="mb-4 rounded-2xl bg-red-50 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
            <div>
              <h3 className="font-medium text-red-800">操作失敗</h3>
              <p className="mt-1 whitespace-pre-wrap text-sm text-red-700">
                {((mutationError || issueEinvoiceMutation.error) as { message?: string })?.message || '發生未知錯誤'}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* 底部固定操作列：每個 state 只有一個主 CTA（藍色 pill），其餘降為次要 */}
      <div className="fixed bottom-16 left-0 right-0 border-t border-[#e8e8ed] bg-white/95 p-4 backdrop-blur safe-area-bottom">
        <div className="flex gap-2">
          {/* 驗收報告 PDF（次要，各狀態皆可） */}
          <button
            onClick={handleDownloadPdf}
            disabled={pdfLoading === 'pdf'}
            className={`shrink-0 ${SECONDARY_BTN_CLASS}`}
          >
            <FileText className="h-4 w-4" />
            {pdfLoading === 'pdf' ? '...' : 'PDF'}
          </button>

          {/* 請款單 PDF（次要，signed 之後） */}
          {['signed', 'submitted', 'invoiced', 'done'].includes(data.state) && (
            <button
              onClick={handleDownloadPayment}
              disabled={pdfLoading === 'payment'}
              className={`shrink-0 ${SECONDARY_BTN_CLASS}`}
            >
              <Receipt className="h-4 w-4" />
              {pdfLoading === 'payment' ? '...' : '請款單'}
            </button>
          )}

          {/* LINE 通知（次要，invoiced/done + 綁定 + 發票齊備） */}
          {data.can_send_line_notify && (
            <button
              onClick={handleNotifyLine}
              disabled={notifyLineMutation.isPending}
              className={`shrink-0 ${SECONDARY_BTN_CLASS}`}
              title="發送 LINE 通知給客戶（含請款包 + 電子發票下載連結）"
            >
              <MessageCircle className="h-4 w-4" />
              {notifyLineMutation.isPending ? '...' : 'LINE'}
            </button>
          )}

          {/* 主 CTA：draft → 簽名完成驗收 */}
          {data.state === 'draft' && (
            <Link
              to={`/acceptance/${data.id}/edit`}
              className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 text-[15px] font-medium text-white"
            >
              <Edit className="h-4 w-4" /> 簽名完成驗收
            </Link>
          )}

          {/* 主 CTA：signed → 送出驗收（先五項核對，確認後自動建應收憑單） */}
          {data.state === 'signed' && (
            <button
              onClick={() => { setShowSubmitDialog(true); setSubmitChecked(false); }}
              disabled={submitMutation.isPending}
              className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 text-[15px] font-medium text-white disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {submitMutation.isPending ? '送出中...' : '送出驗收'}
            </button>
          )}

          {/* 主 CTA：invoiced/done → 請款包 */}
          {(data.state === 'invoiced' || data.state === 'done') && (
            <button
              onClick={handleDownloadPackage}
              disabled={pdfLoading === 'package'}
              className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 text-[15px] font-medium text-white disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              {pdfLoading === 'package' ? '下載中...' : '請款包'}
            </button>
          )}
        </div>
      </div>

      {/* Hidden PDF render targets */}
      {paymentData && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <PaymentRequestPdf ref={pdfRef} data={paymentData} />
        </div>
      )}
      {reportData && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <AcceptanceReportPdf ref={reportRef} data={reportData} />
        </div>
      )}
    </div>
  );
}

function InfoItem({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div>
      <p className="text-xs text-[#86868b]">{label}</p>
      <p className="text-[#1d1d1f]">{value || '---'}</p>
    </div>
  );
}
